import React from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { FlexDiv, Image, SpaceBetweenDiv } from '../design-system/containers';
import { TextDefault } from '../design-system/texts';
import { palette } from '../design-system/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { translatableResourcePath } from '../../utils/helpers';
import { UnStyledCheckboxInput, SpeechBubbleInput } from './components/inputs';
import { isHighlightOnlyAnswer } from './answer-highlight';

function ConversationItem({ ui, node, onChange, groupValue, value, answer }) {
    const { t } = useTranslation('assessment');

    const highlightOnly = isHighlightOnlyAnswer(answer);

    const isCorrect = () => {
        return (
            answer &&
            answer.correctAnswer[value] &&
            answer.correctAnswer[value].score > 0
        );
    };

    const isUserCorrect = () => {
        if (highlightOnly) {
            return isCorrect();
        }
        return (
            answer &&
            answer.correctAnswer[value] &&
            answer.userAnswer[value] &&
            answer.correctAnswer[value].score > 0
        );
    };

    const isIncorrect = () => {
        if (highlightOnly) {
            return false;
        }
        return (
            answer && answer.userAnswer[value] && !answer.correctAnswer[value]
        );
    };

    return (
        <SpaceBetweenDiv gap={16} alignEnd>
            <FlexDiv
                gap={8}
                rowReverse={node.side === 'right'}
                $noWrap
                alignEnd
                pl={node.side === 'right' && 40}
                pr={node.side === 'left' && 40}
                flexGrow={2}
                mr={
                    answer &&
                    !isCorrect() &&
                    !isUserCorrect() &&
                    !isIncorrect() &&
                    36
                }
                borderTop={node.isSelectNoneCheckbox && !answer}
                mt={node.isSelectNoneCheckbox && 24}
                py={node.isSelectNoneCheckbox && !answer && 24}
            >
                {!node.isSelectNoneCheckbox && ( //If this is a 'select none' checkbox, we don't want a thumb
                    <Image
                        alt={t(`person on the ${node.side}`)}
                        src={translatableResourcePath(ui[`${node.side}Thumb`], t)}
                        width={32}
                        height={32}
                        borderRadius={50}
                        border
                    />
                )}
                {!node.isSelectNoneCheckbox ? ( //Display the 'speech bubble' like checkbox label
                    <SpeechBubbleInput
                        label={t(node.text)}
                        groupValue={groupValue}
                        value={value}
                        side={node.side}
                        isUserCorrect={isUserCorrect()}
                        isIncorrect={isIncorrect()}
                        isAnswer={!!answer}
                        onChange={(e) =>
                            onChange(e, value, false, 'select_none')
                        }
                    />
                ) : (
                    /** Display the 'select none' checkbox. */
                    <UnStyledCheckboxInput
                        label={t(node.text)}
                        groupValue={groupValue}
                        value={value}
                        isUserCorrect={isUserCorrect()}
                        isIncorrect={isIncorrect()}
                        isAnswer={!!answer}
                        onChange={(e) => {
                            onChange(e, value, true);
                        }}
                    />
                )}
            </FlexDiv>
            {(isCorrect() || isUserCorrect()) && (
                <TextDefault color={palette.evergreen.aspargus}>
                    <CheckCircleIcon fontSize="small" />
                </TextDefault>
            )}
            {isIncorrect() && (
                <TextDefault color={palette.vibrant.red}>
                    <CancelIcon fontSize="small" />
                </TextDefault>
            )}
        </SpaceBetweenDiv>
    );
}

export default observer(ConversationItem);
